import { Grid, makeStyles, MenuItem } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from 'components/Button/Button';
import DateTimePicker from 'components/DateTimePicker/DateTimePicker';
import TextInput from 'components/TextInput/TextInput';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeJoinEmployeeToProjectDialog,
  setJoinEmployeeToProjectSelectedBusinessId,
} from 'store/projectUsers/actions/project-users.actions';
import {
  selectCurrentUserEmployerProjects,
  selectIsJoiningEmployeeToProject,
  selectJoinEmployeeToProjectResourceOptions,
} from 'store/resources/selectors/resources.selectors';
import { joinExistingEmployeeToProject } from 'store/resources/thunks/joinExistingEmployeeToProject.thunk';

import Loading from 'components/Loading/Loading';
import { uniqBy } from 'lodash';
import { selectWorkCalendarProjectsArray } from 'scenes/WorkDemo/store/selectors/workSelectorsDemo';
import { selectSelectedProjectOrganizationChartItems } from 'store/organizationCharts/selectors/organizationChart.selectors';
import { selectProjectsArray } from 'store/projects/selectors/projectsSelectors';
import {
  selectJoinEmployeeSelectedBusinessId,
  selectJoinEmployeeToProjectDialogMeta,
} from 'store/projectUsers/selectors/projectUsers.selectors';
import { loadResourcesThatHaveJoinedProjectsOfBusiness } from 'store/resources/thunks/loadResourcesThatHaveJoinedProjectsOfBusiness.thunk';

export const JoinExistingEmployeeToProjectDialog = ({ projectId, context }) => {
  const useStyles = makeStyles((theme) => ({
    dialogPaper: {
      ...theme.component.dialogPaper,
      [theme.breakpoints.down('xs')]: {
        ...theme.component.dialogPaperFullscreen,
      },
    },
    dialogContent: {
      paddingTop: theme.spacing(2),
    },
    dialogActions: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  }));

  const isJoiningEmployeeToProject = useSelector(
    selectIsJoiningEmployeeToProject
  );

  const workCalendarProjects = useSelector(selectWorkCalendarProjectsArray);
  const projects = useSelector(selectProjectsArray);

  const projectToJoin = [...workCalendarProjects, ...projects].find(
    (project) => project.id === projectId
  );

  const dispatch = useDispatch();

  const classes = useStyles();

  const { t } = useTranslation('common');

  const selectedJoinEmployeeBusinessId = useSelector(
    selectJoinEmployeeSelectedBusinessId
  );
  const dialogMeta = useSelector(selectJoinEmployeeToProjectDialogMeta);
  const shouldShowRecordDate = Boolean(dialogMeta?.createRecordForUser);

  const onConfirm = () => {
    dispatch(
      joinExistingEmployeeToProject({
        projectId: dialogMeta?.projectId || projectId,
        userId: selectedUserId,
        employerBusinessId: selectedJoinEmployeeBusinessId,
        createRecordForUser: dialogMeta?.createRecordForUser,
        selectedUserData,
        recordDate: shouldShowRecordDate
          ? moment(recordDate).toISOString()
          : undefined,
      })
    );
  };

  const close = () => {
    dispatch(closeJoinEmployeeToProjectDialog());
  };

  const [selectedUserId, setSelectedUserId] = useState('');
  const [recordDate, setRecordDate] = useState(moment());

  const employeeResources = useSelector(
    selectJoinEmployeeToProjectResourceOptions
  );
  const selectedUserData = uniqBy(employeeResources, 'user').find(
    (option) => option.user === selectedUserId
  );

  const userEmployers = useSelector(selectCurrentUserEmployerProjects);

  const organizationChartItemsOfProject = useSelector(
    selectSelectedProjectOrganizationChartItems
  );

  useEffect(() => {
    dispatch(
      loadResourcesThatHaveJoinedProjectsOfBusiness({
        filterEmployerBusinessIds: selectedJoinEmployeeBusinessId,
      })
    );
  }, [selectedJoinEmployeeBusinessId]);

  const effectiveEmployerOptions =
    context === 'ORGANIZATION_CHART'
      ? organizationChartItemsOfProject.map((item) => ({
        name: item.businessName,
        businessId: item.businessId,
      }))
      : userEmployers.map((employer) => ({
        name: employer.name,
        businessId: employer.business_id,
      }));

  return (
    <Dialog
      open={true}
      onClose={() => close()}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>{'Liitä työntekijä projektiin'}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            Projekti: {projectToJoin?.name}
          </Grid>
          <Grid item xs={12}>
            <TextInput
              value={selectedJoinEmployeeBusinessId || ''}
              onChange={(e) =>
                dispatch(
                  setJoinEmployeeToProjectSelectedBusinessId(e.target.value)
                )
              }
              select
              fullWidth
              style={{ marginRight: '4px' }}
              label={'Työnantaja'}
            >
              {effectiveEmployerOptions.map((option, index) => (
                <MenuItem key={index} value={option.businessId}>
                  {`${option.name}`}
                </MenuItem>
              ))}
            </TextInput>
          </Grid>
          <Grid item xs={12}>
            <TextInput
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value)}
              select
              fullWidth
              style={{ marginRight: '4px' }}
              label={'Työntekijä'}
              disabled={!selectedJoinEmployeeBusinessId}
            >
              {uniqBy(employeeResources, 'user').map((option, index) => (
                <MenuItem key={index} value={option.user}>
                  {`${option.firstName} ${option.lastName}`}
                </MenuItem>
              ))}
            </TextInput>
          </Grid>
          {shouldShowRecordDate ? (
            <Grid item xs={12}>
              <DateTimePicker
                type="date"
                onChange={(value) => setRecordDate(value)}
                value={recordDate}
                viewMode="days"
                timeFormat={false}
                inputProps={{
                  label: t('calendar.started_working_at'),
                }}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>{t('common.cancel')}</Button>
        <Button
          disabled={
            !selectedJoinEmployeeBusinessId ||
            !selectedUserId ||
            isJoiningEmployeeToProject
          }
          color="primary"
          variant="contained"
          onClick={() => onConfirm()}
        >
          {isJoiningEmployeeToProject ? (
            <Loading tiny></Loading>
          ) : (
            'Liitä käyttäjä'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
